import { parseAccountFile } from "@/apis/custom/customRoutes";
import type { ParsedAccountRecord } from "@/apis/gql/types";
import { Label } from "@/components/ui/label";
import { useSession } from "@/lib/auth/auth";
import { cn } from "@/lib/utils";
import { useTenant } from "@/providers/session";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { AlertCircle } from "lucide-react";
import { FileText, Loader2, Upload, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { FormValues } from "../form-schema";

interface AccountFileUploadProps {
  label: string;
  accept: string;
  onChange: (file: File | null) => void;
  onAccountsParsed?: (
    accounts: ParsedAccountRecord[],
    accountLength: number,
  ) => void;
  value?: File | null;
}

const LOW_ACCOUNT_COUNT_THRESHOLD = 15;
const HIGH_ACCOUNT_COUNT_THRESHOLD = 1000;

export function AccountFileUpload({
  label,
  accept,
  onChange,
  onAccountsParsed,
  value,
}: AccountFileUploadProps) {
  const [dragActive, setDragActive] = React.useState(false);
  const [file, setFile] = React.useState<File | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [accountCount, setAccountCount] = useState<number | null>(null);
  const [accountLength, setAccountLength] = useState<number | null>(null);
  const [parsingError, setParsingError] = useState<string | null>(null);
  const [detectedAccountLength, setDetectedAccountLength] = useState<
    number | null
  >(null);
  const [showAccountLengthInfo, setShowAccountLengthInfo] = useState(false);
  const { data: session } = useSession();
  const { t } = useTranslation();
  const { getValues } = useFormContext<FormValues>();
  const { companyName } = useTenant();

  // Update local state when value prop changes (for persistence between steps)
  useEffect(() => {
    if (value) {
      setFile(value);
      setParsingError(null);
      // Parse the file when it's set
      parseFile(value);
    } else {
      setFile(null);
      setAccountCount(null);
      setAccountLength(null);
      setDetectedAccountLength(null);
      setShowAccountLengthInfo(false);
      // Don't clear parsingError here - let it persist to show the error message
    }
  }, [value]);

  const parseFile = async (fileToParse: File) => {
    const skrValue = getValues().skr;
    const skrType = skrValue === "03" ? "skr03" : "skr04";

    setIsParsing(true);
    setParsingError(null);
    try {
      const response = await parseAccountFile(
        fileToParse,
        skrType,
        getValues().companyName || companyName,
      );

      if (response?.data && Array.isArray(response.data)) {
        if (response.numberOfAccountsBeforeDeletion === 0) {
          // No accounts found - treat as error
          const errorMessage = t(
            "mandanten_create.account_file_upload.file_processing_error",
            { filename: fileToParse.name },
          );
          setParsingError(errorMessage);
          toast.error(t("mandanten_create.account_file_upload.parsing_error"));

          // Remove the file but keep the error state
          setFile(null);
          setAccountCount(null);
          setAccountLength(null);
          setDetectedAccountLength(null);
          setShowAccountLengthInfo(false);
          onChange(null);

          // Reset the file input value
          const fileInput = document.getElementById(
            `file-${label}`,
          ) as HTMLInputElement;
          if (fileInput) {
            fileInput.value = "";
          }
          return;
        }

        setAccountCount(response.numberOfAccountsBeforeDeletion);
        setAccountLength(response.accountLength);

        const currentFormAccountLength = getValues().accountLength;
        const detectedLength = response.accountLength;

        if (
          currentFormAccountLength &&
          currentFormAccountLength !== detectedLength.toString()
        ) {
          setDetectedAccountLength(detectedLength);
          setShowAccountLengthInfo(true);
        }
        toast.success(
          t("mandanten_create.account_file_upload.accounts_found", {
            count: response.numberOfAccountsBeforeDeletion,
          }),
        );

        if (onAccountsParsed) {
          onAccountsParsed(response.data, detectedLength);
        }
      } else {
        throw new Error(
          t("mandanten_create.account_file_upload.invalid_server_response"),
        );
      }
    } catch (error) {
      console.error("Error parsing account file:", error);
      const errorMessage = t(
        "mandanten_create.account_file_upload.file_processing_error",
        { filename: fileToParse.name },
      );
      setParsingError(errorMessage);
      toast.error(t("mandanten_create.account_file_upload.parsing_error"));

      // Remove the file but keep the error state
      setFile(null);
      setAccountCount(null);
      setAccountLength(null);
      setDetectedAccountLength(null);
      setShowAccountLengthInfo(false);
      onChange(null);

      // Reset the file input value
      const fileInput = document.getElementById(
        `file-${label}`,
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }
    } finally {
      setIsParsing(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      const newFile = e.dataTransfer.files?.[0];
      setFile(newFile);
      onChange(newFile);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files?.[0]) {
      const newFile = e.target.files?.[0];
      setFile(newFile);
      onChange(newFile);
    }
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setFile(null);
    setAccountCount(null);
    setAccountLength(null);
    setDetectedAccountLength(null);
    setShowAccountLengthInfo(false);
    onChange(null);

    // Reset the file input value
    const fileInput = document.getElementById(
      `file-${label}`,
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  // Function to format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Number.parseFloat((bytes / k ** i).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>

      {!file ? (
        // Upload interface when no file is selected
        <div
          className={cn(
            "border-2 border-dashed rounded-lg p-4 text-center cursor-pointer",
            dragActive ? "border-primary" : "border-gray-300",
            "hover:border-primary transition-colors",
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept={accept}
            onChange={handleChange}
            className="hidden"
            id={`file-${label}`}
          />
          <label
            htmlFor={`file-${label}`}
            className="cursor-pointer flex flex-col items-center"
          >
            <Upload className="h-6 w-6 mb-2 text-gray-500" />
            <span className="text-sm font-medium">
              <strong>
                {t("mandanten_create.account_file_upload.click_or_drag")}
              </strong>
            </span>
          </label>
        </div>
      ) : (
        // File preview when a file is selected
        <div className="border rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-gray-100 p-2 rounded">
                <FileText className="h-5 w-5 text-gray-600" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium truncate max-w-[200px]">
                  {file.name}
                </span>
                <span className="text-xs text-gray-500">
                  {formatFileSize(file.size)}
                </span>
                {isParsing && (
                  <div className="flex items-center space-x-2 mt-1">
                    <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                    <span className="text-xs text-blue-500">
                      {t("mandanten_create.account_file_upload.analyzing")}
                    </span>
                  </div>
                )}
                {accountCount !== null && !isParsing && (
                  <div className="flex items-center space-x-2 mt-1">
                    <span
                      className={cn(
                        "text-xs font-medium",
                        accountCount < LOW_ACCOUNT_COUNT_THRESHOLD ||
                          accountCount > HIGH_ACCOUNT_COUNT_THRESHOLD
                          ? "text-yellow-600"
                          : "text-green-600",
                      )}
                    >
                      {t(
                        "mandanten_create.account_file_upload.accounts_found_count",
                        { count: accountCount },
                      )}
                    </span>
                    {accountLength && (
                      <>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-500">
                          {t(
                            "mandanten_create.account_file_upload.account_length",
                            { length: accountLength },
                          )}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={handleRemoveFile}
              className="p-1 rounded-full hover:bg-gray-100"
              title={t("mandanten_create.account_file_upload.remove_file")}
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
        </div>
      )}

      {/* Prominent warning card for low account count */}
      {accountCount !== null &&
        !isParsing &&
        accountCount < LOW_ACCOUNT_COUNT_THRESHOLD && (
          <div className="mt-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-orange-800 mb-1">
                  {t(
                    "mandanten_create.account_file_upload.low_account_count_title",
                    { count: accountCount },
                  )}
                </h4>
                <p className="text-sm text-orange-700">
                  {t(
                    "mandanten_create.account_file_upload.low_account_count_description",
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

      {/* Warning card for high account count */}
      {accountCount !== null &&
        !isParsing &&
        accountCount > HIGH_ACCOUNT_COUNT_THRESHOLD && (
          <div className="mt-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-orange-800 mb-1">
                  {t(
                    "mandanten_create.account_file_upload.high_account_count_title",
                    { count: accountCount },
                  )}
                </h4>
                <p className="text-sm text-orange-700">
                  {t(
                    "mandanten_create.account_file_upload.high_account_count_description",
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

      {/* Error warning card for parsing failures */}
      {parsingError && (
        <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-800 mb-1">
                {t(
                  "mandanten_create.account_file_upload.file_processing_error_title",
                )}
              </h4>
              <p className="text-sm text-red-700">{parsingError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Yellow info card for detected account length */}
      {showAccountLengthInfo && (
        <div className="mt-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-yellow-800 mb-1">
                {t(
                  "mandanten_create.account_file_upload.detected_account_length_title",
                )}
              </h4>
              <p className="text-sm text-yellow-700">
                {t(
                  "mandanten_create.account_file_upload.detected_account_length_description",
                  { length: detectedAccountLength },
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
